import {
  customFieldConfigurationSchema,
  type AddCustomFieldInput,
  type CustomFieldConfiguration,
  type CustomFieldModule,
  type ReorderCustomFieldsInput,
  type UpdateCustomFieldInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';
import { isApiRequestError } from '../api/errors.js';

/**
 * A module's custom-field configuration, over HTTP.
 *
 * Every call answers with the WHOLE configuration rather than the one field it changed. That is
 * deliberate: adding a field renumbers the order, hiding one changes what the sheet renders, and
 * a client that patched a single row into its cache would be showing a list the server no longer
 * agrees with.
 *
 * Every module goes through `/settings/custom-fields/:module`.
 */

function pathFor(module: CustomFieldModule): string {
  return `/settings/custom-fields/${encodeURIComponent(module)}`;
}

export async function getCustomFields(
  client: ApiClient,
  module: CustomFieldModule,
): Promise<CustomFieldConfiguration> {
  return await client.get(pathFor(module), { schema: customFieldConfigurationSchema });
}

export async function addCustomField(
  client: ApiClient,
  module: CustomFieldModule,
  input: AddCustomFieldInput,
): Promise<CustomFieldConfiguration> {
  return await client.post(pathFor(module), input, { schema: customFieldConfigurationSchema });
}

export async function updateCustomField(
  client: ApiClient,
  module: CustomFieldModule,
  fieldKey: string,
  input: UpdateCustomFieldInput,
): Promise<CustomFieldConfiguration> {
  return await client.patch(`${pathFor(module)}/${encodeURIComponent(fieldKey)}`, input, {
    schema: customFieldConfigurationSchema,
  });
}

export async function reorderCustomFields(
  client: ApiClient,
  module: CustomFieldModule,
  input: ReorderCustomFieldsInput,
): Promise<CustomFieldConfiguration> {
  return await client.patch(`${pathFor(module)}/order`, input, {
    schema: customFieldConfigurationSchema,
  });
}

export async function deleteCustomField(
  client: ApiClient,
  module: CustomFieldModule,
  fieldKey: string,
): Promise<CustomFieldConfiguration> {
  return await client.del(`${pathFor(module)}/${encodeURIComponent(fieldKey)}`, undefined, {
    schema: customFieldConfigurationSchema,
  });
}

/**
 * Read a refused delete: how many records are holding this field, or `null` if the error was
 * something else. The 409 is an answer — "somebody filled this in" — and the screen turns it
 * into the offer to hide the field instead. Mirrors `fieldInUseCount` in the measurements API.
 */
export function fieldInUseCount(error: unknown): number | null {
  if (!isApiRequestError(error) || error.status !== 409) return null;
  const details = error.details as { reason?: string; usageCount?: number } | undefined;
  if (details?.reason !== 'field_in_use') return null;
  return typeof details.usageCount === 'number' ? details.usageCount : 0;
}
